import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { memberApi, settingsApi } from '../../services/api';
import { formatDate } from '../../utils/dateUtils';
import { censorEmail } from '../../utils/stringUtils';
import TableFilter from '../../components/TableFilter';
import SortableTable from '../../components/SortableTable';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { Modal, Button, Form } from 'react-bootstrap';
import { toast } from 'react-toastify';
import confirmDialog from '../../components/ConfirmDialog';

const MemberList = () => {
  const { hasPermission } = useAuth();
  const { t } = useTranslation(['members', 'common']);
  const [members, setMembers] = useState([]);
  const [filteredMembers, setFilteredMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    status: 'active', // Default to hide disabled members
    service: '',
    paymentType: '',
    hasFee: ''
  });
  const [uniqueServices, setUniqueServices] = useState([]);
  
  // Batch edit state
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [showBatchEditModal, setShowBatchEditModal] = useState(false);
  const [batchEditData, setBatchEditData] = useState({
    status: '',
    service: '',
    payment_type: '',
    is_free: null,
    monthly_fee: null
  });
  const [savingBatchEdit, setSavingBatchEdit] = useState(false);
  
  // Batch delete state
  const [showBatchDeleteModal, setShowBatchDeleteModal] = useState(false);
  const [deletingBatch, setDeletingBatch] = useState(false);

  useEffect(() => {
    const fetchMembers = async () => {
      try {
        setLoading(true);
        const response = await memberApi.getAll();
        
        // Sort members by username
        const data = response.data.sort((a, b) => {
          const usernameA = (a.username || '').toLowerCase();
          const usernameB = (b.username || '').toLowerCase();
          return usernameA.localeCompare(usernameB);
        });
        
        // Extract unique services from members data
        const services = [...new Set(data.map(member => member.service).filter(Boolean))].sort();
        setUniqueServices(services);
        
        setMembers(data);
        applyFilters(data);
        setLoading(false);
      } catch (err) {
        setError(t('members:loadFailed'));
        setLoading(false);
        console.error(err);
      }
    };

    fetchMembers();
  }, [t]);
  
  // Apply filters when filters change
  useEffect(() => {
    applyFilters(members);
  }, [filters, members]);
  
  // Clear selected members when filtered members change
  useEffect(() => {
    setSelectedMembers(prev => prev.filter(id => 
      filteredMembers.some(member => member.id === id)
    ));
  }, [filteredMembers]);
  
  const applyFilters = (membersToFilter) => {
    let filtered = [...membersToFilter];
    
    // Apply status filter
    if (filters.status) {
      filtered = filtered.filter(member => member.status === filters.status);
    }
    
    // Apply service filter
    if (filters.service) {
      filtered = filtered.filter(member => (member.service || '') === filters.service);
    }
    
    // Apply payment type filter
    if (filters.paymentType) {
      filtered = filtered.filter(member => member.payment_type === filters.paymentType);
    }
    
    // Apply fee filter
    if (filters.hasFee === 'free') {
      filtered = filtered.filter(member => member.is_free);
    } else if (filters.hasFee === 'paid') {
      filtered = filtered.filter(member => !member.is_free);
    }
    
    setFilteredMembers(filtered);
  };

  const handleDelete = async (id) => {
    confirmDialog({
      message: t('members:confirmDelete'),
      onConfirm: async () => {
        try {
          await memberApi.delete(id);
          const updatedMembers = members.filter(member => member.id !== id);
          setMembers(updatedMembers);
          setFilteredMembers(updatedMembers);
          toast.success(t('members:deleteSuccess', { name: members.find(m => m.id === id)?.name || members.find(m => m.id === id)?.username || 'Unknown' }));
        } catch (err) {
          toast.error(t('members:deleteFailed'));
          console.error(err);
        }
      }
    });
  };
  
  const handleToggleStatus = async (id, newStatus) => {
    try {
      // First update the member in the database
      await memberApi.update(id, { status: newStatus });
      
      // Get the updated member from the database to ensure we have the latest data
      const response = await memberApi.getById(id);
      const updatedMember = response.data;
      
      // Update the member in the local state
      const updatedMembers = members.map(member => {
        if (member.id === id) {
          return updatedMember;
        }
        return member;
      });
      
      setMembers(updatedMembers);
      
      // Show a success message
      setError(null); // Clear any previous errors
      const actionKey = newStatus === 'active' ? 'enable' : 'disable';
      toast.success(`${updatedMember.name || updatedMember.username || 'Unknown'} ${t(`members:actions.${actionKey}d`)}`);
      
      // Reapply all filters to ensure the list is updated correctly
      applyFilters(updatedMembers);
      
    } catch (err) {
      const actionKey = newStatus === 'active' ? 'enable' : 'disable';
      toast.error(`${t(`members:actions.${actionKey}`)} ${t('common:messages.failed')}`);
      console.error(err);
    }
  };

  const handleFilter = (filterText) => {
    // Apply all filters including the search text
    let filtered = [...members];
    
    // Apply dropdown filters first
    if (filters.status) {
      filtered = filtered.filter(member => member.status === filters.status);
    }
    
    if (filters.service) {
      filtered = filtered.filter(member => (member.service || '') === filters.service);
    }
    
    if (filters.paymentType) {
      filtered = filtered.filter(member => member.payment_type === filters.paymentType);
    }
    
    if (filters.hasFee === 'free') {
      filtered = filtered.filter(member => member.is_free);
    } else if (filters.hasFee === 'paid') {
      filtered = filtered.filter(member => !member.is_free);
    }
    
    // Then apply text search
    if (filterText) {
      const lowercasedFilter = filterText.toLowerCase();
      
      // Check for "missing" keyword to find members with missing email
      if (lowercasedFilter === 'missing') {
        filtered = filtered.filter(member => !member.email);
      } else {
        filtered = filtered.filter(member => (
          (member.name ? member.name.toLowerCase().includes(lowercasedFilter) : false) ||
          (member.username ? member.username.toLowerCase().includes(lowercasedFilter) : false) ||
          (member.email ? member.email.toLowerCase().includes(lowercasedFilter) : false) ||
          (member.service ? member.service.toLowerCase().includes(lowercasedFilter) : false)
        ));
      }
    }
    
    setFilteredMembers(filtered);
  };
  
  // Handle member selection
  const handleSelectMember = (memberId, isSelected) => {
    if (isSelected) {
      setSelectedMembers([...selectedMembers, memberId]);
    } else {
      setSelectedMembers(selectedMembers.filter(id => id !== memberId));
    }
  };
  
  // Handle select all
  const handleSelectAll = (isSelected) => {
    if (isSelected) {
      setSelectedMembers(filteredMembers.map(member => member.id));
    } else {
      setSelectedMembers([]);
    }
  };
  
  // Handle batch delete
  const handleBatchDelete = async () => {
    try {
      setDeletingBatch(true);
      
      // Call the batch delete API
      const result = await memberApi.batchDelete(selectedMembers);
      
      // Refresh the member list
      const response = await memberApi.getAll();
      setMembers(response.data);
      applyFilters(response.data);
      
      // Reset selection and close modal
      setSelectedMembers([]);
      setShowBatchDeleteModal(false);
      
      // Show success message
      toast.success(t('members:batchDelete.deleteSuccess', { 
        count: result.success_count,
        total: selectedMembers.length
      }));
      
      // Show warning if some deletions failed
      if (result.failed_ids && result.failed_ids.length > 0) {
        toast.warning(t('members:batchDelete.partialSuccess', { 
          failed: result.failed_ids.length
        }));
      }
    } catch (err) {
      toast.error(t('members:batchDelete.deleteFailed'));
      console.error(err);
    } finally {
      setDeletingBatch(false);
    }
  };
  
  // Handle batch update
  const handleBatchUpdate = async () => {
    // Filter out empty fields to avoid unintentional updates
    const updateData = Object.fromEntries(
      Object.entries(batchEditData).filter(([_, v]) => v !== '' && v !== null)
    );
    
    if (Object.keys(updateData).length === 0) {
      toast.warning(t('common:messages.noChanges'));
      return;
    }
    
    // Handle special values
    Object.keys(updateData).forEach(key => {
      if (updateData[key] === '__clear__') {
        updateData[key] = null;
      }
    });
    
    try {
      setSavingBatchEdit(true);
      
      await memberApi.batchUpdate(selectedMembers, updateData);
      
      // Refresh the member list
      const response = await memberApi.getAll();
      setMembers(response.data);
      applyFilters(response.data);
      
      // Reset selection and close modal
      setSelectedMembers([]);
      setShowBatchEditModal(false);
      setBatchEditData({
        status: '',
        service: '',
        payment_type: '',
        is_free: null,
        monthly_fee: null
      });
      
      // Show success message
      toast.success(t('members:batchEdit.updateSuccess', { count: selectedMembers.length }));
    } catch (err) {
      toast.error(t('members:batchEdit.updateFailed'));
      console.error(err);
    } finally {
      setSavingBatchEdit(false);
    }
  };
  
  const columns = useMemo(() => [
    {
      key: 'select',
      label: (
        <input 
          type="checkbox" 
          checked={selectedMembers.length === filteredMembers.length && filteredMembers.length > 0}
          onChange={(e) => handleSelectAll(e.target.checked)}
          aria-label={t('members:batchEdit.selectAll')}
        />
      ),
      sortable: false,
      style: { width: '1%' },
      render: (member) => (
        <input 
          type="checkbox" 
          checked={selectedMembers.includes(member.id)}
          onChange={(e) => handleSelectMember(member.id, e.target.checked)}
          aria-label={t('common:actions.select')}
        />
      )
    },
    {
      key: 'username',
      label: t('members:fields.username'),
      sortable: false,
      render: (member) => member.username || '-'
    },
    {
      key: 'service',
      label: t('members:fields.service'),
      sortable: false,
      render: (member) => member.service || ''
    },
    {
      key: 'status',
      label: t('members:fields.status'),
      sortable: false,
      render: (member) => (
        <span className={`badge ${member.status === 'active' ? 'bg-success' : 'bg-danger'}`}>
          {t(`members:status.${member.status}`)}
        </span>
      )
    },
    {
      key: 'payment_type',
      label: t('members:fields.paymentType'),
      sortable: false,
      render: (member) => (
        member.payment_type ? t(`members:paymentTypes.${member.payment_type}`) : '-'
      )
    },
    {
      key: 'current_fee',
      label: t('members:fields.currentFee'),
      sortable: false,
      render: (member) => {
        const fee = member.monthly_fee_history && member.monthly_fee_history.length > 0
          ? `${member.monthly_fee_history[member.monthly_fee_history.length - 1].amount.toFixed(2)} €`
          : 'N/A';
        return (
          <>
            {fee}
            {member.is_free && ` (${t('members:filters.free')})`}
          </>
        );
      }
    },
    {
      key: 'actions',
      label: t('common:actions.actions'),
      sortable: false,
      className: 'text-end',
      headerClassName: 'text-end',
      style: { width: '1%', whiteSpace: 'nowrap' },
      render: (member) => (
        <div className="btn-group float-end">
          <Link to={`/members/${member.id}`} className="btn btn-sm btn-outline-primary" title={t('members:actions.view')}>
            <i className="fas fa-eye"></i>
          </Link>
          {hasPermission('members', 'update') && (
            <Link to={`/members/${member.id}/edit`} className="btn btn-sm btn-outline-success" title={t('members:actions.edit')}>
              <i className="fas fa-edit"></i>
            </Link>
          )}
          {hasPermission('members', 'update') && (
            <button 
              onClick={() => handleToggleStatus(member.id, member.status === 'active' ? 'disabled' : 'active')} 
              className={`btn btn-sm ${member.status === 'active' ? 'btn-outline-warning' : 'btn-outline-success'}`}
              title={t(`members:actions.${member.status === 'active' ? 'disable' : 'enable'}`)}
            >
              <i className={`fas ${member.status === 'active' ? 'fa-ban' : 'fa-check'}`}></i>
            </button>
          )}
          {hasPermission('members', 'delete') && (
            <button 
              onClick={() => handleDelete(member.id)} 
              className="btn btn-sm btn-outline-danger"
              title={t('members:actions.delete')}
            >
              <i className="fas fa-trash-alt"></i>
            </button>
          )}
        </div>
      )
    }
  ], [hasPermission, selectedMembers, filteredMembers, t]);

  if (loading) {
    return <div className="text-center py-5"><i className="fas fa-spinner fa-spin fa-2x"></i></div>;
  }

  if (error) {
    return <div className="alert alert-danger"><i className="fas fa-exclamation-triangle me-2"></i>{error}</div>;
  }

  return (
    <div className="container-fluid">
      <div className="row mb-4">
        <div className="col">
          <div className="d-flex justify-content-between align-items-center">
            <h1 className="display-5 mb-0">{t('members:title')}</h1>
            <div className="d-flex gap-2">
              {selectedMembers.length > 0 && (
                <>
                  {hasPermission('members', 'update') && (
                    <Button 
                      variant="warning" 
                      onClick={() => setShowBatchEditModal(true)}
                    >
                      <i className="fas fa-edit me-2"></i>
                      {t('members:batchEdit.batchEditButton', { count: selectedMembers.length })}
                    </Button>
                  )}
                  {hasPermission('members', 'delete') && (
                    <Button 
                      variant="danger" 
                      onClick={() => setShowBatchDeleteModal(true)}
                    >
                      <i className="fas fa-trash-alt me-2"></i>
                      {t('members:batchDelete.batchDeleteButton', { count: selectedMembers.length })}
                    </Button>
                  )}
                </>
              )}
              {hasPermission('payments', 'view') && (
                <Link to="/payments" className="btn btn-primary">
                  <i className="fas fa-credit-card me-2"></i>
                  {t('common:navigation.payments')}
                </Link>
              )}
              {hasPermission('members', 'create') && (
                <Link to="/members/new" className="btn btn-success">
                  <i className="fas fa-user-plus me-2"></i>
                  {t('members:addMember')}
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>

      {members.length === 0 ? (
        <div className="row">
          <div className="col">
            <div className="alert alert-info d-flex align-items-center">
              <i className="fas fa-info-circle me-3 fs-4"></i>
              <div>
                {t('members:noMembers')}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="row">
          <div className="col">
            <div className="card shadow-sm">
              <div className="card-header bg-white py-3">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="card-title mb-0">
                    <i className="fas fa-users me-2"></i>
                    {t('members:title')}
                    {selectedMembers.length > 0 && (
                      <span className="ms-2 badge bg-warning text-dark">
                        {t('members:batchEdit.selected', { count: selectedMembers.length })}
                      </span>
                    )}
                  </h5>
                  <div className="d-flex gap-2 align-items-center">
                    <select 
                      className="form-select form-select-sm" 
                      value={filters.status}
                      onChange={(e) => setFilters({...filters, status: e.target.value})}
                    >
                      <option value="">{t('members:filters.allStatus')}</option>
                      <option value="active">{t('members:filters.activeOnly')}</option>
                      <option value="disabled">{t('members:filters.disabledOnly')}</option>
                    </select>
                    
                    <select 
                      className="form-select form-select-sm" 
                      value={filters.service}
                      onChange={(e) => setFilters({...filters, service: e.target.value})}
                    >
                      <option value="">{t('members:filters.allServices')}</option>
                      {uniqueServices.map(service => (
                        <option key={service} value={service}>{service}</option>
                      ))}
                    </select>
                    
                    <select 
                      className="form-select form-select-sm" 
                      value={filters.paymentType}
                      onChange={(e) => setFilters({...filters, paymentType: e.target.value})}
                    >
                      <option value="">{t('members:filters.allPaymentTypes')}</option>
                      <option value="PP">{t('members:paymentTypes.PP')}</option>
                      <option value="Ü">{t('members:paymentTypes.Ü')}</option>
                      <option value="+">{t('members:paymentTypes.+')}</option>
                      <option value="-">{t('members:paymentTypes.-')}</option>
                    </select>
                    
                    <select 
                      className="form-select form-select-sm" 
                      value={filters.hasFee}
                      onChange={(e) => setFilters({...filters, hasFee: e.target.value})}
                    >
                      <option value="">{t('members:filters.allFeeStatus')}</option>
                      <option value="paid">{t('members:filters.hasFee')}</option>
                      <option value="free">{t('members:filters.free')}</option>
                    </select>
                    
                    <span className="badge bg-primary rounded-pill px-3 py-2 fs-6">
                      {filteredMembers.length} {t('members:title').toLowerCase()}
                    </span>
                  </div>
                </div>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    {selectedMembers.length > 0 && (
                      <div className="btn-group">
                        <Button 
                          variant="outline-primary" 
                          size="sm" 
                          onClick={() => handleSelectAll(true)}
                        >
                          <i className="fas fa-check-square me-1"></i>
                          {t('members:batchEdit.selectAll')}
                        </Button>
                        <Button 
                          variant="outline-secondary" 
                          size="sm" 
                          onClick={() => handleSelectAll(false)}
                        >
                          <i className="fas fa-square me-1"></i>
                          {t('members:batchEdit.deselectAll')}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="card-body p-0">
                <div className="p-3 border-bottom">
                  <TableFilter 
                    onFilter={handleFilter} 
                    placeholder={t('members:filters.searchPlaceholder')}
                  />
                </div>
                <div className="table-responsive">
                  <SortableTable 
                    columns={columns} 
                    data={filteredMembers} 
                    keyField="id"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Batch Edit Modal */}
      <Modal show={showBatchEditModal} onHide={() => setShowBatchEditModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-edit me-2"></i>
            {t('members:batchEdit.title', { count: selectedMembers.length })}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted mb-3">
            {t('members:batchEdit.description')}
          </p>
          
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>{t('members:fields.status')}</Form.Label>
              <Form.Select 
                value={batchEditData.status}
                onChange={(e) => setBatchEditData({...batchEditData, status: e.target.value})}
              >
                <option value="">{t('members:batchEdit.noChange')}</option>
                <option value="active">{t('members:status.active')}</option>
                <option value="disabled">{t('members:status.disabled')}</option>
              </Form.Select>
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>{t('members:fields.service')}</Form.Label>
              <Form.Select 
                value={batchEditData.service}
                onChange={(e) => setBatchEditData({...batchEditData, service: e.target.value})}
              >
                <option value="">{t('members:batchEdit.noChange')}</option>
                {uniqueServices.map(service => (
                  <option key={service} value={service}>{service}</option>
                ))}
                <option value="__clear__">{t('members:batchEdit.clearField')}</option>
              </Form.Select>
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>{t('members:fields.paymentType')}</Form.Label>
              <Form.Select 
                value={batchEditData.payment_type}
                onChange={(e) => setBatchEditData({...batchEditData, payment_type: e.target.value})}
              >
                <option value="">{t('members:batchEdit.noChange')}</option>
                <option value="PP">{t('members:paymentTypes.PP')}</option>
                <option value="Ü">{t('members:paymentTypes.Ü')}</option>
                <option value="+">{t('members:paymentTypes.+')}</option>
                <option value="-">{t('members:paymentTypes.-')}</option>
                <option value="__clear__">{t('members:batchEdit.clearField')}</option>
              </Form.Select>
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>{t('members:fields.currentFee')}</Form.Label>
              <Form.Control 
                type="number" 
                step="0.01"
                placeholder={t('members:batchEdit.noChange')}
                value={batchEditData.monthly_fee === null ? '' : batchEditData.monthly_fee}
                onChange={(e) => {
                  const value = e.target.value;
                  setBatchEditData({
                    ...batchEditData, 
                    monthly_fee: value === '' ? null : parseFloat(value)
                  });
                }}
              />
              <Form.Text className="text-muted">
                {t('common:messages.leaveEmptyForNoChange')}
              </Form.Text>
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>{t('members:filters.free')}</Form.Label>
              <Form.Select 
                value={batchEditData.is_free === null ? '' : batchEditData.is_free ? 'true' : 'false'}
                onChange={(e) => {
                  const value = e.target.value;
                  setBatchEditData({
                    ...batchEditData, 
                    is_free: value === '' ? null : value === 'true'
                  });
                }}
              >
                <option value="">{t('members:batchEdit.noChange')}</option>
                <option value="true">{t('common:yes')}</option>
                <option value="false">{t('common:no')}</option>
              </Form.Select>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowBatchEditModal(false)}>
            {t('members:batchEdit.cancel')}
          </Button>
          <Button 
            variant="primary" 
            onClick={handleBatchUpdate}
            disabled={savingBatchEdit}
          >
            {savingBatchEdit ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                {t('common:messages.saving')}
              </>
            ) : (
              <>
                <i className="fas fa-save me-2"></i>
                {t('members:batchEdit.update', { count: selectedMembers.length })}
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
      
      {/* Batch Delete Modal */}
      <Modal show={showBatchDeleteModal} onHide={() => setShowBatchDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-trash-alt me-2"></i>
            {t('members:batchDelete.title', { count: selectedMembers.length })}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="alert alert-danger">
            <i className="fas fa-exclamation-triangle me-2"></i>
            {t('members:batchDelete.warning')}
          </div>
          
          <p>
            {t('members:batchDelete.confirmMessage', { count: selectedMembers.length })}
          </p>
          
          <p className="fw-bold">
            {t('members:batchDelete.confirmQuestion')}
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowBatchDeleteModal(false)}>
            {t('members:batchDelete.cancel')}
          </Button>
          <Button 
            variant="danger" 
            onClick={handleBatchDelete}
            disabled={deletingBatch}
          >
            {deletingBatch ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                {t('common:messages.deleting')}
              </>
            ) : (
              <>
                <i className="fas fa-trash-alt me-2"></i>
                {t('members:batchDelete.confirm', { count: selectedMembers.length })}
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default MemberList;
